import { TableAction, TableColumn } from '@/types';
import { UserListItem } from '@/services/userService';
import RoleBadge from '@/components/ui/RoleBadge.tsx';
import StatusBadge from '@/components/ui/StatusBadge.tsx';
import CreditsModal from '@/components/ui/CreditsModal.tsx';
import { Edit, UserCheck, UserX, History, Coins, Plus, Search } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery.tsx';
import { useTranslation, useAuth } from '@/hooks';
import { useUserList, useToggleUserStatus, useUser } from '@/hooks';
import { useState, useEffect, useMemo } from 'react';
import { dateWithMonthName } from '@/utils/formatters.ts';
import { RemainingCreditsBadge } from '@/components/ui/RemainingCreditsBadge.tsx';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect.tsx';
import Button from '@/components/ui/Button.tsx';
import Input from '@/components/ui/Input.tsx';
import UserCreatorLabel from '@/components/ui/UserCreatorLabel';

const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const toggleUserStatusMutation = useToggleUserStatus();
  // Get status filter from URL parameters
  const statusFilter = searchParams.get('status');
  const roleFilter = searchParams.get('role');
  const createdByUserIdFilter = searchParams.get('createdByUserId');
  const normalizedRoleFilter = roleFilter === 'user' ? 'app' : roleFilter;

  // Build initial filters based on URL parameters
  const initialFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};

    if (statusFilter === 'expired') {
      filters.expired = true;
    } else if (statusFilter === 'aboutToExpire') {
      filters.aboutToExpire = true;
    }
    if (normalizedRoleFilter) {
      filters["role.code"] = normalizedRoleFilter;
    }
    if (createdByUserIdFilter) {
      filters.createdByUserId = createdByUserIdFilter;
    }

    return filters;
  }, [statusFilter, normalizedRoleFilter, createdByUserIdFilter]);

  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [openCreditsHandled, setOpenCreditsHandled] = useState(false);
  const openCreditsUserId = searchParams.get('openCredits');
  const { data: openCreditsUser } = useUser(openCreditsUserId ?? '');
  const { data: sellersData } = useUserList({ "role.code": 'agent', _size: 1000 });

  const roleOptions = useMemo(
    () => [
      { value: 'user', label: 'Usuario' },
      { value: 'agent', label: 'Agente' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'expired', label: 'Vencidos' },
      { value: 'aboutToExpire', label: 'Por vencer' },
    ],
    []
  );

  const sellerOptions = useMemo(
    () =>
      (sellersData?.content ?? []).map((seller) => ({
        value: String(seller.id),
        label: seller.fullName,
      })),
    [sellersData]
  );

  const updateFilterParam = (key: 'role' | 'status' | 'createdByUserId', value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }

    setSearchParams(nextParams);
  };

  const handleToggleUserStatus = async (user: UserListItem) => {
    if(user.role?.code == 'app' && user.credits <= 0 && user.status === 'INACTIVE') {
      alert('No se puede activar un usuario sin créditos.');
      return;
    }

    try {
      await toggleUserStatusMutation.mutateAsync(user.username);
      console.log(`User ${user.username} status toggled successfully`);
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleOpenCreditsModal = (user: UserListItem) => {
    setSelectedUser(user);
    setCreditsModalOpen(true);
  };

  const handleCloseCreditsModal = () => {
    setCreditsModalOpen(false);
    setSelectedUser(null);
    // Clear only openCredits so current filters stay intact.
    if (searchParams.has('openCredits')) {
      setOpenCreditsHandled(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('openCredits');
      setSearchParams(nextParams);
    }
  };

  useEffect(() => {
    if (!openCreditsUserId) {
      setOpenCreditsHandled(false);
      return;
    }

    if (!openCreditsHandled && openCreditsUser) {
      setSelectedUser(openCreditsUser);
      setCreditsModalOpen(true);
      setOpenCreditsHandled(true);
    }
  }, [openCreditsUserId, openCreditsUser, openCreditsHandled]);

  const userColumns: TableColumn<UserListItem>[] = [
    {
      sortable: false,
      key: '#',
      header: '#',
      accessor: (_user: UserListItem, index) => (
        <span className="text-gray-900">
          {index? index  + 1 : 1}
        </span>
      ),
      className: 'hidden md:table-cell'
    },
    {
      sortable: true,
      key: 'username',
      header: 'Nombre',
      accessor: (user) => (
        <div className="flex items-center">
          <div className="hidden h-8 w-8 bg-indigo-100 rounded-full items-center justify-center md:mr-3 md:flex">
            <span className="text-sm font-medium text-indigo-700">
              {user.fullName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900">
              <Link to={`/users/${user.id}/edit`} className="link">
                {user.fullName}
                <div className="hover:no-underline no-underline">
                  <span className="text-sm text-gray-500 hover:no-underline no-underline">{user.username}</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      sortable: true,
      key: 'role',
      header: 'Rol',
      accessor: (user) => <RoleBadge role={user?.role}/>
    },
    {
      sortable: true,
      key: 'expirationDate',
      header: 'Créditos',
      accessor: (user) => (
        <span className="font-medium text-gray-900">
          <RemainingCreditsBadge user={user} />
        </span>
      )
    },
    {
      sortable: true,
      key: 'expirationDate',
      header: 'Vence',
      accessor: (user) => (
        <span className="text-gray-900">
          {dateWithMonthName(user.expirationDate, 'No Vence')}
        </span>
      )
    },
    {
      sortable: true,
      key: 'signIns',
      header: 'Entradas',
      accessor: (user) => (
        <span className="font-medium text-gray-900">
          {user.role?.code === 'app' ?  user.accessCount : user.signIns}
        </span>
      )
    },
    {
      sortable: true,
      key: 'status',
      header: 'Estado',
      accessor: (user) => <StatusBadge status={user.status}/>
    },
    {
      sortable: true,
      key: 'createdDate',
      header: t.users.registration,
      accessor: (user) => (
        <div className="text-gray-900">
          <span>{dateWithMonthName(user.createdDate, 'No Disponible')}</span>
          <UserCreatorLabel user={user} compact />
        </div>
      ),
    }
  ];

  const userActions: TableAction<UserListItem>[] = [
    {
      label: 'Editar',
      icon: Edit,
      onClick: (user) => navigate(`/users/${user.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Créditos',
      icon: Coins,
      onClick: handleOpenCreditsModal,
      className: 'text-gray-700 hover:text-indigo-600',
      show: (user) => user.username !== currentUser?.username && ['app', 'agent'].includes(user.role?.code || '')
    },
    {
      label: 'Historial',
      icon: History,
      onClick: (user) => console.log('View user:', user.username),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Activar',
      icon: UserCheck,
      onClick: handleToggleUserStatus,
      className: 'text-gray-700 hover:text-green-600',
      show: () => false
    },
    {
      label: 'Desactivar',
      icon: UserX,
      onClick: handleToggleUserStatus,
      className: 'text-gray-700 hover:text-yellow-600',
      show: () => false
    },
  ];

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={userColumns}
          actions={userActions}
          actionsMobileIconOnly
          useQueryHook={useUserList}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{t.users.listTitle}</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateUser} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo usuario
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    size="sm"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="pl-10 pr-4"
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <AutocompleteSelect
                      value={roleFilter === 'app' ? 'user' : (roleFilter ?? '')}
                      onChange={(value) => updateFilterParam('role', value)}
                      options={roleOptions}
                      placeholder="Todos los roles"
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                  <div className="min-w-0">
                    <AutocompleteSelect
                      value={statusFilter ?? ''}
                      onChange={(value) => updateFilterParam('status', value)}
                      options={statusOptions}
                      placeholder="Todos los estados"
                      size="sm"
                      className="text-base"
                      allowClear
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={createdByUserIdFilter ?? ''}
                    onChange={(value) => updateFilterParam('createdByUserId', value)}
                    options={sellerOptions}
                    placeholder="Vendedor"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">{t.users.listTitle}</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    size="sm"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="pl-10 pr-4"
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={roleFilter === 'app' ? 'user' : (roleFilter ?? '')}
                    onChange={(value) => updateFilterParam('role', value)}
                    options={roleOptions}
                    placeholder="Todos los roles"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={statusFilter ?? ''}
                    onChange={(value) => updateFilterParam('status', value)}
                    options={statusOptions}
                    placeholder="Todos los estados"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                <div className="min-w-[180px]">
                  <AutocompleteSelect
                    value={createdByUserIdFilter ?? ''}
                    onChange={(value) => updateFilterParam('createdByUserId', value)}
                    options={sellerOptions}
                    placeholder="Vendedor"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateUser} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo usuario
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateUser}
          searchPlaceholder={t.users.placeholders.search}
          title={t.users.listTitle}
          itemsPerPage={15}
          initialFilters={initialFilters}
          className="rounded-none shadow-none border border-gray-200"
        />
      </div>
      {selectedUser && (
        <CreditsModal
          isOpen={creditsModalOpen}
          onClose={handleCloseCreditsModal}
          user={selectedUser}
        />
      )}
    </div>
  );
}

export default Users;
